import type { ActionDef, Clause, EntityType, FallbackSpec, FxSpec, InteractionRule, ProgramStep, ResultEffect, RoleSpec, StepTrack } from '@/api/actions'
import { ENTITY_TYPES, prettyId } from '@/api/actions'

/**
 * Plain words for the action registry (docs/SKEL-STORY.md §11). Pure functions, no React: every
 * sentence a grown-up reads on the Actions page — who takes part, what they need, what happens step
 * by step, what is true afterwards, and when a world rule fires — is made here, so it is tested once
 * and reads the same on a card, a detail page and the create-with-AI review.
 *
 * Refs (`$thrower`, `$catcher.socket.hand`, `spot:left`, `$pond.edge`) become "the thrower", "the
 * catcher's hand", "the left spot", "the edge of the pond". Unknown shapes never throw: they fall
 * back to their JSON, which is still more useful than nothing.
 */

/* -------------------------------------------------------------------------- */
/* Entity types                                                                */
/* -------------------------------------------------------------------------- */

/** How a participant type is called in the UI. `feature` is a place on the stage. */
export const TYPE_LABEL: Record<EntityType, string> = {
  character: 'Character',
  object: 'Object',
  scenery: 'Scenery',
  feature: 'Place',
  spot: 'Spot',
}

const TYPE_NOUN: Record<EntityType, string> = {
  character: 'character',
  object: 'object',
  scenery: 'piece of scenery',
  feature: 'place',
  spot: 'spot on the stage',
}

const KIND_NOUN: Record<string, string> = {
  person: 'person',
  animal: 'animal',
  bird: 'bird',
  fish: 'fish',
  creature: 'creature',
  object: 'object',
}

const isEntityType = (t: unknown): t is EntityType => typeof t === 'string' && (ENTITY_TYPES as readonly string[]).includes(t)

/** §9's older type words, and the four §9.1 role names, mapped onto §11's entity types. */
const LEGACY_TYPE: Record<string, EntityType> = { actor: 'character', prop: 'object', place: 'feature', region: 'feature', anchor: 'feature' }
const LEGACY_ROLE: Record<string, EntityType[]> = { actor: ['character'], partner: ['character'], object: ['object'], target: ['spot', 'character', 'object'] }

/** The entity types a role accepts; `[]` = anything. */
export function roleTypes(name: string, spec: RoleSpec | null | undefined): EntityType[] {
  const raw = Array.isArray(spec?.types) ? spec.types : null
  if (raw && raw.length > 0) {
    const out: EntityType[] = []
    for (const t of raw) {
      const m = isEntityType(t) ? t : LEGACY_TYPE[t as string]
      if (m && !out.includes(m)) out.push(m)
    }
    return out
  }
  if (spec?.kinds_any?.length || spec?.plans_any?.length || spec?.needs?.length) {
    // Kinds and sockets only make sense on puppets that act.
    const objectish = spec.kinds_any?.length && spec.kinds_any.every((k) => k === 'object')
    return [objectish ? 'object' : 'character']
  }
  return LEGACY_ROLE[name] ?? []
}

/* -------------------------------------------------------------------------- */
/* Words                                                                       */
/* -------------------------------------------------------------------------- */

const words = (s: string): string => s.replace(/[_-]+/g, ' ').trim()

function article(noun: string): string {
  return /^[aeiou]/i.test(noun) && !/^(one|use|uni)/i.test(noun) ? `an ${noun}` : `a ${noun}`
}

/** `["a","b","c"]` → "a, b or c" (or "and"). */
export function joinWords(list: readonly string[], conj: 'or' | 'and' = 'or'): string {
  const xs = list.filter(Boolean)
  if (xs.length <= 1) return xs[0] ?? ''
  return `${xs.slice(0, -1).join(', ')} ${conj} ${xs[xs.length - 1]}`
}

const cap = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)

/** The role name as a noun: `ball` → "the ball", `the_boy` → "the boy". */
export function roleNoun(role: string): string {
  const w = words(role)
  return w.startsWith('the ') ? w : `the ${w}`
}

/** Capability → a relative clause that reads after "that": "can be thrown", "floats". */
const CAPABILITY: Record<string, string> = {
  holdable: 'can be held',
  throwable: 'can be thrown',
  kickable: 'can be kicked',
  rollable: 'rolls',
  bounces: 'bounces',
  edible: 'can be eaten',
  rideable: 'can be ridden',
  floats: 'floats',
  flies: 'flies',
  container: 'can hold things',
  openable: 'opens',
  wearable: 'can be worn',
  climbable: 'can be climbed',
  sittable: 'can be sat on',
  standable_on: 'can be stood on',
  hideable_behind: 'can be hidden behind',
  shakeable: 'can be shaken',
  swimmable: 'can be swum in',
  splashable: 'splashes',
  floatable_in: 'things float in',
  drinkable: 'can be drunk from',
  enterable: 'can be walked through',
  flyable: 'can be flown through',
  has_hands: 'has hands',
  can_swim: 'can swim',
  can_fly: 'can fly',
  can_climb: 'can climb',
}

export function capabilityPhrase(cap0: string): string {
  const c = cap0.replace(/\?$/, '')
  const known = CAPABILITY[c]
  if (known) return known
  if (c.startsWith('can_') || c.startsWith('has_')) return words(c)
  return `is ${words(c)}`
}

/** Sockets a participant needs → "a hand", "a hand and a mouth". */
function socketList(needs: readonly string[]): string {
  return joinWords(
    needs.map((n) => article(words(n))),
    'and',
  )
}

/* -------------------------------------------------------------------------- */
/* Refs                                                                        */
/* -------------------------------------------------------------------------- */

export interface Names {
  /** Role name → how to say it ("the thrower", "it"). Missing roles use `roleNoun`. */
  [role: string]: string
}

function nameOf(role: string, names: Names | undefined): string {
  return names?.[role] ?? roleNoun(role)
}

const possessive = (noun: string): string => (noun === 'it' ? 'its' : /s$/.test(noun) ? `${noun}'` : `${noun}'s`)

/**
 * `$catcher.socket.hand` → "the catcher's hand"; `$pond.edge` → "the edge of the pond";
 * `spot:left` → "the left spot"; `$actor.feet` → "the actor's feet"; anything else → itself.
 */
export function describeRef(ref: unknown, names?: Names): string {
  if (typeof ref !== 'string' || ref === '') return 'somewhere'
  if (ref.startsWith('spot:')) return `the ${words(ref.slice(5))} spot`
  if (!ref.startsWith('$')) return words(ref)
  const [head = '', ...rest] = ref.slice(1).split('.')
  const who = nameOf(head, names)
  if (rest.length === 0) return who
  const [a = '', b] = rest
  if (a === 'socket' && b) return `${possessive(who)} ${words(b)}`
  switch (a) {
    case 'pos':
      return `where ${who} is`
    case 'feet':
      return `${possessive(who)} feet`
    case 'edge':
      return `the edge of ${who}`
    case 'center':
    case 'centre':
      return `the middle of ${who}`
    case 'surface':
      return `the surface of ${who}`
    case 'top':
      return `the top of ${who}`
    default:
      return `${possessive(who)} ${words(rest.join(' '))}`
  }
}

/* -------------------------------------------------------------------------- */
/* State                                                                       */
/* -------------------------------------------------------------------------- */

function valueWords(v: unknown, names?: Names): string {
  if (typeof v === 'string') return v.startsWith('$') || v.startsWith('spot:') ? describeRef(v, names) : words(v)
  if (v === null || v === undefined) return 'nothing'
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  return JSON.stringify(v)
}

/**
 * "who's key is value" in plain words, e.g. `holder = $catcher` → "the ball is held by the catcher",
 * `wet = true` → "the boy is wet", `mood = happy` → "the fox feels happy".
 */
export function stateSentence(who: string, key: string, value: unknown, opts: { negate?: boolean; now?: boolean; names?: Names } = {}): string {
  const is = opts.now ? (opts.negate ? 'is no longer' : 'is now') : opts.negate ? 'is not' : 'is'
  const v = valueWords(value, opts.names)
  const none = value === null || value === 'none' || value === false
  switch (key) {
    case 'holder':
      if (none) return `${who} ${opts.negate ? 'is held by someone' : opts.now ? 'is now held by no one' : 'is not held by anyone'}`
      if (value === 'any') return `${who} ${opts.negate ? 'is not held' : 'is held by someone'}`
      return `${who} ${is} held by ${v}`
    case 'holding':
      if (none) return `${who} ${opts.negate ? 'is holding something' : opts.now ? 'now has empty hands' : 'has empty hands'}`
      if (value === 'any') return `${who} ${opts.negate ? 'has empty hands' : 'is holding something'}`
      return `${who} ${is} holding ${v}`
    case 'mounted_on':
      return none ? `${who} ${opts.negate ? 'is riding something' : 'is not riding anything'}` : `${who} ${is} riding ${v}`
    case 'carrying':
      return none ? `${who} ${opts.negate ? 'is carrying something' : 'is not carrying anything'}` : `${who} ${is} carrying ${v}`
    case 'inside':
      return none ? `${who} ${opts.negate ? 'is inside something' : 'is not inside anything'}` : `${who} ${is} inside ${v}`
    case 'hidden_behind':
      return none ? `${who} ${opts.negate ? 'is hiding' : 'is not hiding'}` : `${who} ${is} hiding behind ${v}`
    case 'mood':
      return `${who} ${opts.negate ? 'does not feel' : opts.now ? 'now feels' : 'feels'} ${v}`
    case 'pos':
      return `${who} ${is} at ${v}`
    case 'facing':
      return `${who} ${is} facing ${v}`
    case 'visible':
      if (value === true) return `${who} ${opts.negate ? 'is hidden' : opts.now ? 'is now visible' : 'is visible'}`
      if (value === false) return `${who} ${opts.negate ? 'is visible' : opts.now ? 'is now hidden' : 'is hidden'}`
      break
    default:
      break
  }
  if (value === true) return `${who} ${is} ${words(key)}`
  if (value === false) return `${who} ${opts.now ? (opts.negate ? 'is again' : 'is no longer') : opts.negate ? 'is' : 'is not'} ${words(key)}`
  return `${possessive(who)} ${words(key)} ${opts.now ? (opts.negate ? 'is no longer' : 'becomes') : opts.negate ? 'is not' : 'is'} ${v}`
}

/** `$ball.holder` → `["ball", "holder"]`; also the §9 dotted form `object.holder`. */
function splitStateRef(ref: string): [string, string] {
  const s = ref.startsWith('$') ? ref.slice(1) : ref
  const i = s.indexOf('.')
  return i < 0 ? [s, ''] : [s.slice(0, i), s.slice(i + 1)]
}

/* -------------------------------------------------------------------------- */
/* Roles                                                                       */
/* -------------------------------------------------------------------------- */

/** "a character", "an object or a piece of scenery", "a person or an animal", "anything". */
export function typePhrase(name: string, spec: RoleSpec | null | undefined): string {
  const kinds = (spec?.kinds_any ?? []).filter((k) => typeof k === 'string' && k)
  if (kinds.length > 0) return joinWords(kinds.map((k) => article(KIND_NOUN[k] ?? words(k))))
  const types = roleTypes(name, spec)
  if (types.length === 0) return 'anything'
  return joinWords(types.map((t) => article(TYPE_NOUN[t])))
}

/** The requirements of one role as short phrases (no subject): "can be thrown", "needs a hand". */
export function roleRequirements(spec: RoleSpec | null | undefined, names?: Names, who = 'it'): string[] {
  if (!spec) return []
  const out: string[] = []
  if (spec.plans_any?.length) out.push(`has ${article(joinWords(spec.plans_any.map(words)))} body`)
  if (spec.capabilities_all?.length) out.push(joinWords(spec.capabilities_all.map(capabilityPhrase), 'and'))
  if (spec.capabilities_any?.length) out.push(joinWords(spec.capabilities_any.map(capabilityPhrase), 'or'))
  if (spec.needs?.length) out.push(`needs ${socketList(spec.needs)}`)
  if (spec.state && typeof spec.state === 'object') {
    for (const [k, v] of Object.entries(spec.state)) out.push(stateSentence(who, k, v, { names }).replace(new RegExp(`^${escapeRe(who)} `), ''))
  }
  if (typeof spec.held_by === 'string' && spec.held_by) {
    const hb = spec.held_by
    out.push(hb === 'none' ? 'is not held by anyone' : hb === 'any' ? 'is held by someone' : `is held by ${describeRef(hb.startsWith('$') ? hb : `$${hb}`, names)}`)
  }
  return out.filter(Boolean)
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export interface RoleSummary {
  role: string
  title: string
  types: EntityType[]
  required: boolean
  /** "A character that has hands and needs a hand." */
  sentence: string
}

/** One participant in plain words. */
export function describeRole(role: string, spec: RoleSpec | null | undefined, names?: Names): RoleSummary {
  const reqs = roleRequirements(spec, names)
  const required = spec?.required !== false
  const head = typePhrase(role, spec)
  const sentence = `${cap(head)}${reqs.length ? ` that ${joinWords(reqs, 'and')}` : ''}${required ? '' : ' (optional)'}.`
  return { role, title: cap(words(role)), types: roleTypes(role, spec), required, sentence }
}

/* -------------------------------------------------------------------------- */
/* Clauses                                                                     */
/* -------------------------------------------------------------------------- */

const RELATION: Record<string, string> = {
  near: 'is near',
  far: 'is far from',
  holding: 'is holding',
  held_by: 'is held by',
  on: 'is on',
  inside: 'is inside',
  behind: 'is behind',
  taller_than: 'is taller than',
  shorter_than: 'is shorter than',
  can_reach: 'can reach',
  same_region: 'is in the same place as',
  in_region: 'is in',
}

const ROLE_CLAUSE_KEYS = ['types', 'kinds_any', 'plans_any', 'capabilities_all', 'capabilities_any', 'needs', 'state', 'held_by'] as const

/** One precondition in plain words. */
export function describeClause(c: Clause | null | undefined, names?: Names): string {
  if (!c || typeof c !== 'object') return 'Something must be true.'
  const k = c.k
  switch (k) {
    case 'role':
    case 'actor':
    case 'partner':
    case 'object':
    case 'target': {
      const role = k === 'role' ? String(c.role ?? '') : k
      const who = nameOf(role, names)
      const spec = c as unknown as RoleSpec
      const reqs = roleRequirements(spec, names, who)
      // §9.2's role clauses carry their own spellings.
      if (typeof c.holding === 'string') reqs.push(stateSentence(who, 'holding', c.holding === '$object' ? '$object' : c.holding, { names }).replace(`${who} `, ''))
      if (Array.isArray(c.has_socket)) reqs.push(`needs ${socketList(c.has_socket as string[])}`)
      else if (typeof c.has_socket === 'string') reqs.push(`needs ${article(words(c.has_socket))}`)
      if (typeof c.has_clip === 'string') reqs.push(`can ${words(c.has_clip)}`)
      if (c.not_hidden === true || c.visible === true) reqs.push('can be seen')
      if (Array.isArray(c.types) || Array.isArray(c.kinds_any)) reqs.unshift(`is ${typePhrase(role, spec)}`)
      const known = ROLE_CLAUSE_KEYS.some((key) => key in c) || reqs.length > 0
      return known && reqs.length ? `${cap(who)} ${joinWords(reqs, 'and')}.` : `${cap(who)} must be there.`
    }
    case 'relation': {
      const rel = String(c.rel ?? '')
      const a = describeRef(c.a, names)
      const b = describeRef(c.b, names)
      const phrase = RELATION[rel] ?? `is ${words(rel)}`
      const ratio = typeof c.ratio === 'number' ? ` (by ${c.ratio}×)` : ''
      return `${cap(a)} ${phrase} ${b}${ratio}.`
    }
    case 'stage': {
      const has = Array.isArray(c.has) ? (c.has as unknown[]).filter((x): x is string => typeof x === 'string') : typeof c.has_region === 'string' ? [c.has_region] : []
      const worlds = Array.isArray(c.world_any) ? (c.world_any as string[]) : []
      const parts: string[] = []
      if (has.length) parts.push(`The place has ${joinWords(has.map(words), 'and')}`)
      if (worlds.length) parts.push(`${parts.length ? 'and' : 'The story'} is set in ${joinWords(worlds.map(words))}`)
      return parts.length ? `${parts.join(' ')}.` : 'Any place works.'
    }
    case 'state': {
      const ref = String(c.ref ?? '')
      const [role, key] = splitStateRef(ref)
      const who = nameOf(role, names)
      if ('eq' in c) return `${cap(stateSentence(who, key, c.eq, { names }))}.`
      if ('ne' in c) return `${cap(stateSentence(who, key, c.ne, { negate: true, names }))}.`
      if (Array.isArray(c.in)) return `${cap(possessive(who))} ${words(key)} is ${joinWords((c.in as unknown[]).map((v) => valueWords(v, names)))}.`
      return `${cap(possessive(who))} ${words(key)} is set.`
    }
    case 'any': {
      const list = Array.isArray(c.any) ? (c.any as Clause[]) : []
      if (list.length === 0) return 'Anything goes.'
      return `At least one of these: ${list.map((x) => lowerFirst(stripDot(describeClause(x, names)))).join('; or ')}.`
    }
    case 'not': {
      const inner = c.not as Clause | undefined
      return `It is not true that ${lowerFirst(stripDot(describeClause(inner, names)))}.`
    }
    default:
      return `${cap(words(String(k ?? 'check')))}: ${JSON.stringify(omit(c, 'k'))}`
  }
}

const stripDot = (s: string): string => s.replace(/\.$/, '')
const lowerFirst = (s: string): string => (s && !/^[A-Z]{2}/.test(s) ? s.charAt(0).toLowerCase() + s.slice(1) : s)

function omit(o: Record<string, unknown>, key: string): Record<string, unknown> {
  const out = { ...o }
  delete out[key]
  return out
}

/* -------------------------------------------------------------------------- */
/* Steps                                                                       */
/* -------------------------------------------------------------------------- */

const PHASE: Record<string, string> = { anticipate: 'Get ready', action: 'Do it', settle: 'Settle down', react: 'React' }

const STOP: Record<string, string> = {
  beside: 'goes next to',
  at: 'goes to',
  behind: 'goes behind',
  on: 'gets onto',
  inside: 'goes inside',
  edge: 'goes to the edge of',
  across: 'goes into and across',
}

/** Keys of a §9.3 step that are not role tracks. */
const STEP_KEYS = new Set(['phase', 'dur', 'fx', 'wait', 'tracks', 'mount', 'note', 'label'])

/** The role tracks of a step: §11.4 `tracks`, or §9.3's per-role keys. */
export function stepTracks(step: ProgramStep | null | undefined): Array<[string, StepTrack]> {
  if (!step || typeof step !== 'object') return []
  if (step.tracks && typeof step.tracks === 'object') return Object.entries(step.tracks).filter((e): e is [string, StepTrack] => !!e[1] && typeof e[1] === 'object')
  return Object.entries(step).filter((e): e is [string, StepTrack] => !STEP_KEYS.has(e[0]) && !!e[1] && typeof e[1] === 'object' && !Array.isArray(e[1]))
}

function clipWord(clip: string): string {
  return `“${words(clip)}”`
}

/** What one participant does in a step, as sentences. */
export function describeTrack(role: string, t: StepTrack, names?: Names): string[] {
  const who = nameOf(role, names)
  const out: string[] = []
  if (t.move && typeof t.move === 'object') {
    const to = describeRef(t.move.to, names)
    const verb = STOP[String(t.move.stop ?? '')] ?? 'goes to'
    const how = t.move.clip && t.move.clip !== 'move' ? ` (${words(t.move.clip)})` : ''
    out.push(`${who} ${verb} ${to}${how}`)
  }
  if (typeof t.face === 'string' && t.face) out.push(`${who} turns to ${describeRef(t.face, names)}`)
  if (typeof t.clip === 'string' && t.clip) {
    const loops = typeof t.loops === 'number' && t.loops > 1 ? ` ${t.loops} times` : ''
    const layer = t.layer === 'upper' || (typeof t.mask === 'string' && t.mask.startsWith('upper')) ? ' with the upper body' : ''
    out.push(`${who} plays ${clipWord(t.clip)}${layer}${loops}`)
  }
  if (t.attach && typeof t.attach === 'object') {
    const to = t.attach.to ? describeRef(t.attach.to, names) : 'someone'
    out.push(`${who} goes to ${t.attach.socket ? `${possessive(to)} ${words(t.attach.socket)}` : to}`)
  }
  if (t.detach && typeof t.detach === 'object') {
    const at = t.detach.at ? ` at ${describeRef(t.detach.at, names)}` : ''
    out.push(`${who} is let go${at}${t.detach.fall ? ' and falls' : ''}`)
  }
  if (t.toss && typeof t.toss === 'object') {
    const from = t.toss.from ? ` from ${describeRef(t.toss.from, names)}` : ''
    const to = t.toss.to ? ` to ${describeRef(t.toss.to, names)}` : ''
    const then = t.toss.then === 'catch' ? ' and is caught' : t.toss.then === 'land' ? ' and lands' : t.toss.then === 'vanish' ? ' and disappears' : ''
    const when = typeof t.toss.at_event === 'string' && t.toss.at_event ? ` ${eventWhen(t.toss.at_event, names)}` : ''
    out.push(`${who} flies${from}${to}${then}${when}`)
  }
  if (t.mount && typeof t.mount === 'object') out.push(`${who} climbs onto ${describeRef(t.mount.on, names)}`)
  if (t.dismount) out.push(`${who} gets off`)
  if (t.vanish === true) out.push(`${who} disappears`)
  if (t.appear) {
    const at = typeof t.appear === 'string' ? ` at ${describeRef(t.appear, names)}` : typeof t.appear === 'object' && t.appear && 'at' in t.appear ? ` at ${describeRef((t.appear as { at?: unknown }).at, names)}` : ''
    out.push(`${who} appears${at}`)
  }
  if (t.react && typeof t.react === 'object') {
    const bits: string[] = []
    if (typeof t.react.clip === 'string' && t.react.clip) bits.push(`plays ${clipWord(t.react.clip)}`)
    if (typeof t.react.fx === 'string' && t.react.fx) bits.push(`shows ${fxWord(t.react.fx)}`)
    out.push(`${who} reacts${bits.length ? ` and ${joinWords(bits, 'and')}` : ''}`)
  }
  if (t.sync && typeof t.sync === 'object' && typeof t.sync.with === 'string') out.push(`${who} times it ${eventWhen(t.sync.with, names)}`)
  return out.map(cap)
}

/** `thrower:release` → "when the thrower lets go"; `ball:arrive` → "when the ball arrives". */
export function eventWhen(ev: string, names?: Names): string {
  const [role, name] = ev.includes(':') ? (ev.split(':', 2) as [string, string]) : ['', ev]
  const who = role ? nameOf(role, names) : 'it'
  const verb: Record<string, string> = {
    release: 'lets go',
    contact: 'touches',
    arrive: 'arrives',
    land: 'lands',
    catch: 'catches',
    impact: 'hits',
    apex: 'is at the top',
  }
  return `when ${who} ${verb[name] ?? `reaches “${words(name)}”`}`
}

const FX: Record<string, string> = {
  sparkle: 'sparkles',
  hearts: 'hearts',
  stars: 'stars',
  dust: 'a puff of dust',
  splash: 'a splash',
  bubbles: 'bubbles',
  confetti: 'confetti',
  zzz: 'sleepy z’s',
  notes: 'music notes',
  question: 'a question mark',
  exclaim: 'an exclamation mark',
  crumbs: 'crumbs',
  wind: 'a gust of wind',
  leaves: 'falling leaves',
}

export function fxWord(kind: string): string {
  return FX[kind] ?? words(kind)
}

export function describeFx(fx: FxSpec, names?: Names): string {
  const at = fx.at ? ` at ${describeRef(fx.at, names)}` : ''
  const when = typeof fx.at_s === 'number' ? ` after ${fmtSeconds(fx.at_s)}` : typeof fx.on === 'string' && fx.on ? ` on ${words(fx.on)}` : ''
  return `${cap(fxWord(fx.kind))}${at}${when}`
}

export function fmtSeconds(s: number): string {
  return `${Number.isInteger(s) ? s : s.toFixed(1).replace(/\.0$/, '')} s`
}

export interface StepSummary {
  title: string
  /** "about 1.2 s", "as long as the move takes". */
  length: string | null
  lines: string[]
}

export function describeStep(step: ProgramStep, index: number, names?: Names): StepSummary {
  const phase = typeof step.phase === 'string' && step.phase ? PHASE[step.phase] ?? cap(words(step.phase)) : `Step ${index + 1}`
  const d = step.dur
  const length = typeof d === 'number' ? `about ${fmtSeconds(d)}` : d === 'clip' ? 'as long as the move takes' : d === 'auto' ? 'as long as it needs' : typeof d === 'string' && d ? `about ${d} s` : null
  const lines: string[] = []
  for (const [role, track] of stepTracks(step)) lines.push(...describeTrack(role, track, names))
  // §9.3's step-level mount.
  const m = step.mount as { rider?: string; on?: string } | null | undefined
  if (m && typeof m === 'object' && m.rider) lines.push(`${cap(describeRef(m.rider, names))} climbs onto ${describeRef(m.on, names)}`)
  for (const fx of Array.isArray(step.fx) ? step.fx : []) if (fx && typeof fx.kind === 'string') lines.push(describeFx(fx, names))
  if (typeof step.wait === 'number' && step.wait > 0) lines.push(`Then a pause of ${fmtSeconds(step.wait)}`)
  if (lines.length === 0) lines.push('A short pause')
  return { title: phase, length, lines }
}

/* -------------------------------------------------------------------------- */
/* Results + fallbacks                                                         */
/* -------------------------------------------------------------------------- */

export function describeResult(r: ResultEffect, names?: Names): string {
  if (!r || typeof r.set !== 'string') return JSON.stringify(r)
  const [role, key] = splitStateRef(r.set)
  const who = nameOf(role, names)
  if (!key) return `${cap(who)} changes.`
  return `${cap(stateSentence(who, key, r.to, { now: true, names }))}.`
}

export function describeFallback(f: FallbackSpec): string {
  return `If it can’t happen, try “${words(f.action)}” instead.`
}

/* -------------------------------------------------------------------------- */
/* Whole documents                                                             */
/* -------------------------------------------------------------------------- */

export interface ActionSummary {
  roles: RoleSummary[]
  needs: string[]
  steps: StepSummary[]
  after: string[]
  fallbacks: string[]
  duration: string | null
  effects: string[]
}

export function describeAction(a: Pick<ActionDef, 'roles'> & Partial<ActionDef>): ActionSummary {
  const roles = Object.entries(a.roles ?? {}).map(([name, spec]) => describeRole(name, spec))
  const needs = (Array.isArray(a.preconditions) ? a.preconditions : []).map((c) => describeClause(c))
  const steps = (Array.isArray(a.program) ? a.program : []).map((s, i) => describeStep(s, i))
  const after = (Array.isArray(a.result) ? a.result : []).map((r) => describeResult(r))
  const fallbacks = (Array.isArray(a.fallback) ? a.fallback : []).filter((f) => f && typeof f.action === 'string').map(describeFallback)
  const d = a.duration
  let duration: string | null = null
  if (d && (typeof d.default === 'number' || typeof d.min === 'number' || typeof d.max === 'number')) {
    const base = typeof d.default === 'number' ? `About ${fmtSeconds(d.default)}` : 'Takes'
    const range = typeof d.min === 'number' && typeof d.max === 'number' ? ` (${fmtSeconds(d.min).replace(' s', '')}–${fmtSeconds(d.max)})` : ''
    duration = `${base}${range}`
  }
  const effects = (Array.isArray(a.fx_auto) ? a.fx_auto : []).filter((f) => f && typeof f.kind === 'string').map((f) => describeFx(f))
  return { roles, needs, steps, after, fallbacks, duration, effects }
}

const EVENT_VERB: Record<string, string> = {
  land: 'lands on',
  enter: 'goes into',
  leave: 'leaves',
  contact: 'touches',
  drop: 'is dropped on',
  arrive: 'arrives at',
}

/** Rule participants are "subject" and "other"; name them by what they are. */
export function ruleNames(rule: Pick<InteractionRule, 'when'>): Names {
  const subj = rule.when?.subject ?? null
  const other = rule.when?.other ?? null
  const noun = (spec: RoleSpec | null, fallback: string): string => {
    const kinds = spec?.kinds_any ?? []
    if (kinds.length === 1 && kinds[0]) return `the ${KIND_NOUN[kinds[0]] ?? words(kinds[0])}`
    const ts = roleTypes('', spec)
    if (ts.length === 1 && ts[0]) return `the ${TYPE_NOUN[ts[0]]}`
    return fallback
  }
  const s = noun(subj, 'the thing')
  let o = noun(other, 'the other thing')
  if (o === s) o = o.replace(/^the /, 'the other ')
  return { subject: s, other: o }
}

export interface RuleSummary {
  when: string
  subject: string | null
  other: string | null
  then: StepSummary[]
  after: string[]
  priority: string | null
}

/** "When an object that floats lands on a place that things float in" + its parts. */
export function describeRule(rule: Pick<InteractionRule, 'when' | 'then'> & Partial<InteractionRule>): RuleSummary {
  const names = ruleNames(rule)
  const w = rule.when ?? { event: '' }
  const subjSpec = w.subject ?? null
  const otherSpec = w.other ?? null
  const subjReqs = roleRequirements(subjSpec, names)
  const otherReqs = roleRequirements(otherSpec, names)
  const subjPhrase = `${typePhrase('', subjSpec)}${subjReqs.length ? ` that ${joinWords(subjReqs, 'and')}` : ''}`
  const otherPhrase = otherSpec ? `${typePhrase('', otherSpec)}${otherReqs.length ? ` that ${joinWords(otherReqs, 'and')}` : ''}` : null
  const ev = String(w.event ?? '')
  let when: string
  if (ev === 'state_change') {
    const st = w.state && typeof w.state === 'object' ? Object.entries(w.state) : []
    when = st.length
      ? `When ${joinWords(
          st.map(([k, v]) => stateSentence(subjPhrase, k, v, { now: true, names })),
          'and',
        )}`
      : `When something about ${subjPhrase} changes`
  } else if (ev === 'drop' && !otherPhrase) {
    when = `When ${subjPhrase} is dropped`
  } else {
    const verb = EVENT_VERB[ev] ?? `${words(ev)}s`
    when = `When ${subjPhrase} ${verb} ${otherPhrase ?? 'something'}`
  }
  const then = (Array.isArray(rule.then) ? rule.then : []).map((s, i) => describeStep(s, i, names))
  const after = (Array.isArray(rule.result) ? rule.result : []).map((r) => describeResult(r, names))
  const bits: string[] = []
  if (typeof rule.priority === 'number') bits.push(`priority ${rule.priority}`)
  if (rule.once_per_pair) bits.push('once for each pair')
  return {
    when,
    subject: subjSpec ? describeRole('subject', subjSpec, names).sentence : null,
    other: otherSpec ? describeRole('other', otherSpec, names).sentence : null,
    then,
    after,
    priority: bits.length ? cap(joinWords(bits, 'and')) : null,
  }
}

/** Title for a rule card when the rule has no `label`. */
export function ruleTitle(rule: Pick<InteractionRule, 'id'> & Partial<InteractionRule>): string {
  return rule.label || prettyId(rule.id)
}

/* -------------------------------------------------------------------------- */
/* Filter labels                                                               */
/* -------------------------------------------------------------------------- */

const CATEGORY_LABEL: Record<string, string> = {
  play: 'Play',
  social: 'Friends',
  locomotion: 'Getting around',
  reaction: 'Feelings',
  rest: 'Rest',
  twist: 'Surprises',
}

const EVENT_LABEL: Record<string, string> = {
  land: 'Lands',
  enter: 'Goes in',
  leave: 'Leaves',
  contact: 'Touches',
  drop: 'Dropped',
  arrive: 'Arrives',
  state_change: 'Changes',
}

export const categoryLabel = (c: string): string => CATEGORY_LABEL[c] ?? prettyId(c)
export const eventLabel = (e: string): string => EVENT_LABEL[e] ?? prettyId(e)
